import { FormEvent, useState } from "react";
import Link from "next/link";
import { Waves } from "lucide-react";
import { useRouter } from "next/router";
import { LoadingButton } from "@/components/ui/loading-button";
import { authClient } from "@/lib/auth-client";
import { getApiErrorCode, getApiErrorMessage } from "@/store/api";
import { useCreateBillingCheckoutMutation } from "@/store/billing-api";
import {
  getCheckoutInterval,
  getQuery,
  withAuthContext,
} from "@/components/boardblend/auth-context";
import {
  type SaveSessionTrigger,
  useSaveSessionMutation,
} from "@/store/entry-api";

type AuthMode = "forgot" | "reset" | "sign-in" | "sign-up" | "verify";

export function AuthForm({ mode }: { mode: AuthMode }) {
  const router = useRouter();
  const [saveSession, saveState] = useSaveSessionMutation();
  const [createCheckout, checkoutState] =
    useCreateBillingCheckoutMutation();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    setMessage("");
    saveState.reset();

    try {
      if (mode === "sign-in") {
        const result = await authClient.signIn.email({ email, password });
        if (result.error) throw new Error(result.error.message);
        await finishAuthentication(router, saveSession, createCheckout);
        return;
      }
      if (mode === "sign-up") {
        const callbackURL = getVerificationCallbackURL(router.query);
        const result = await authClient.signUp.email({
          callbackURL,
          email,
          name,
          password,
        });
        if (result.error) throw new Error(result.error.message);
        await router.push(
          withAuthContext("/verify-email?sent=true", router.query),
        );
        return;
      }
      if (mode === "forgot") {
        const result = await authClient.requestPasswordReset({
          email,
          redirectTo: `${window.location.origin}/reset-password`,
        });
        if (result.error) throw new Error(result.error.message);
        setMessage("Check your email for a reset link.");
      }
      if (mode === "reset") {
        const token = getQuery(router.query.token);
        if (!token) throw new Error("This reset link is invalid");
        const result = await authClient.resetPassword({
          newPassword: password,
          token,
        });
        if (result.error) throw new Error(result.error.message);
        setMessage("Password updated. You can sign in now.");
      }
      if (mode === "verify") {
        const result = await authClient.sendVerificationEmail({
          callbackURL: getVerificationCallbackURL(router.query),
          email,
        });
        if (result.error) throw new Error(result.error.message);
        setMessage("Verification email sent.");
      }
    } catch (cause) {
      setError(getApiErrorMessage(cause, "Something went wrong"));
    } finally {
      setBusy(false);
    }
  };

  const content = authContent[mode];
  const signInPath = withAuthContext("/sign-in", router.query);

  if (mode === "reset" && message) {
    return (
      <main className="auth-screen">
        <section className="auth-card grid gap-6">
          <p role="status">Password updated. You can sign in now.</p>
          <LoadingButton
            type="button"
            onClick={async () => {
              await router.push(signInPath);
            }}
          >
            Sign in
          </LoadingButton>
        </section>
      </main>
    );
  }

  return (
    <main className="auth-screen">
      <section className="auth-card">
        <Link href="/" className="auth-brand">
          <span className="brand-mark">
            <Waves />
          </span>
          <strong>Boardblend</strong>
        </Link>
        <p className="eyebrow">{content.eyebrow}</p>
        <h1>{content.heading}</h1>
        {content.copy && <p>{content.copy}</p>}
        <form onSubmit={submit}>
          {mode === "sign-up" && (
            <>
              <label htmlFor="name">Name</label>
              <input
                id="name"
                value={name}
                onChange={(event) => setName(event.target.value)}
                required
                maxLength={80}
              />
            </>
          )}
          {mode !== "reset" && (
            <>
              <label htmlFor="email">Email</label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                required
                autoComplete="email"
              />
            </>
          )}
          {(mode === "sign-in" || mode === "sign-up" || mode === "reset") && (
            <>
              <label htmlFor="password">
                {mode === "reset" ? "New password" : "Password"}
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                required
                minLength={8}
                autoComplete={
                  mode === "sign-in" ? "current-password" : "new-password"
                }
              />
            </>
          )}
          {error && (
            <p className="form-error" role="alert">
              {error}
            </p>
          )}
          {message && (
            <p className="form-success" role="status">
              {message}
            </p>
          )}
          <LoadingButton
            type="submit"
            loading={busy || saveState.isLoading || checkoutState.isLoading}
          >
            {content.action}
          </LoadingButton>
        </form>
        {mode === "sign-in" && (
          <div className="auth-links">
            <Link href={withAuthContext("/sign-up", router.query)}>
              Create account
            </Link>
            <Link href="/forgot-password">Forgot password?</Link>
          </div>
        )}
        {mode === "sign-up" && (
          <p className="legal-copy">
            By creating an account, you agree to our{" "}
            <Link href="/terms">Terms</Link> and{" "}
            <Link href="/privacy">Privacy Policy</Link>.
          </p>
        )}
        {(mode === "forgot" || mode === "reset" || mode === "verify") && (
          <Link
            className="auth-back-link"
            href={signInPath}
          >
            Back to sign in
          </Link>
        )}
      </section>
    </main>
  );
}

const authContent = {
  forgot: {
    action: "Send reset link",
    copy: "We’ll email you a secure reset link.",
    eyebrow: "Account recovery",
    heading: "Reset your password",
  },
  reset: {
    action: "Update password",
    copy: "Choose a new password for your account.",
    eyebrow: "Account recovery",
    heading: "Choose a new password",
  },
  "sign-in": {
    action: "Sign in",
    copy: "",
    eyebrow: "Welcome back",
    heading: "Sign in",
  },
  "sign-up": {
    action: "Create account",
    copy: "",
    eyebrow: "Save your work",
    heading: "Create your account",
  },
  verify: {
    action: "Send verification email",
    copy: "Check your inbox, or enter your account email for a fresh link.",
    eyebrow: "Email verification",
    heading: "Verify your email",
  },
} as const;

export async function finishAuthentication(
  router: ReturnType<typeof useRouter>,
  saveSession: SaveSessionTrigger,
  createCheckout: CheckoutTrigger,
) {
  const sessionId = getQuery(router.query.saveSession);
  if (sessionId) await saveSession(sessionId).unwrap();

  const checkout = getCheckoutInterval(router.query);
  if (checkout) {
    try {
      const result = await createCheckout({
        interval: checkout,
        returnTo: "/dashboard",
      }).unwrap();
      window.location.assign(result.url);
    } catch (error) {
      if (getApiErrorCode(error) !== "SUBSCRIPTION_EXISTS") throw error;
      await router.push("/dashboard?notice=subscription-exists");
    }
    return;
  }

  const returnTo = getQuery(router.query.returnTo);
  await router.push(returnTo?.startsWith("/") && !returnTo.startsWith("//") ? returnTo : "/dashboard");
}

function getVerificationCallbackURL(
  query: ReturnType<typeof useRouter>["query"],
) {
  return `${window.location.origin}${withAuthContext("/verify-email?verified=true", query)}`;
}

type CheckoutTrigger = (args: {
  interval: "monthly";
  returnTo: string;
}) => { unwrap: () => Promise<{ url: string }> };
